/**
 * Own-tenant scope: while a Globaladmin or reseller has their own tenant selected (the token's `tenant_id`, the
 * default after sign-in), only the admin pages below are available. Every other page works on one customer
 * tenant's data, so the user must first pick a tenant in Tenant Management. Selecting any other tenant lifts this.
 * Client-side navigation gating only; each API still enforces its own authorization.
 */

// Sidebar menu key -> its page; `path` and everything under it is allowed (compared case-insensitively, like the router).
export const OWN_TENANT_PAGES: Record<string, { label: string; path: string }> = {
  TenantManagement: { label: 'Tenant Management', path: '/customers' },
  Settings: { label: 'Settings', path: '/settings' },
  AccessControl: { label: 'Access Control', path: '/Accounts' },
  ActivityLog: { label: 'Activity Log', path: '/activity-log' },
  ScriptCenter: { label: 'Script Center', path: '/ScriptAutomation' },
};

// Menus available while each role has its own tenant selected.
export const OWN_TENANT_MENUS: Record<string, string[]> = {
  globaladmin: ['TenantManagement', 'Settings', 'AccessControl', 'ActivityLog', 'ScriptCenter'],
  reseller: ['TenantManagement', 'ActivityLog'],
};

// Pages that never depend on a selected tenant.
const ALWAYS_ALLOWED_ROUTES = ['/login'];

const sameTenant = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** True when the role is limited in its own tenant and no other tenant is selected (none selected counts as own). */
export const isOwnTenantScope = (
  role: string | undefined,
  selectedTenantId: string | null | undefined,
  ownTenantId: string | null | undefined,
) => !!role && role in OWN_TENANT_MENUS && (!selectedTenantId || sameTenant(selectedTenantId, ownTenantId));

/** The sidebar menu keys a role may use in its own tenant scope. */
export const ownTenantMenus = (role: string): string[] => OWN_TENANT_MENUS[role] ?? [];

const matchesRoute = (pathname: string, route: string) => {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const base = route.toLowerCase();
  return path === base || path.startsWith(`${base}/`);
};

/** Whether `pathname` is one of the role's own-tenant pages (or a page that needs no tenant). */
export const isRouteAllowedInOwnTenant = (role: string, pathname: string): boolean => {
  const routes = [
    ...ALWAYS_ALLOWED_ROUTES,
    ...ownTenantMenus(role)
      .map((key) => OWN_TENANT_PAGES[key]?.path)
      .filter(Boolean),
  ];
  return routes.some((route) => matchesRoute(pathname, route as string));
};
